import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  CustomerDemandResponse,
  DashboardKPIs,
  DeliveryPerformanceResponse,
  DocType,
  ExceptionStats,
  InventoryResponse,
  OrderHistoryResponse,
  POLinesResponse,
  ShipToContact,
  SubmissionDocument,
  SubmissionListFilters,
  SubmissionListResponse,
  RoutingMode,
  SubmissionSummary,
} from './types'

const BASE = '/api/v1/uviva'
const UVIVA_KEY = ['uviva'] as const

// ── Dashboard ──────────────────────────────────────────────────────────────────

export function useDashboardKPIs() {
  return useQuery({
    queryKey: [...UVIVA_KEY, 'kpis'],
    queryFn: () => jsonFetch<DashboardKPIs>(`${BASE}/dashboard/kpis`),
    staleTime: 30_000,
    refetchInterval: 60_000,
  })
}

// ── Submissions ────────────────────────────────────────────────────────────────

export function useSubmissionList(filters: SubmissionListFilters = {}) {
  const params = new URLSearchParams()
  if (filters.status) params.set('status', filters.status)
  if (filters.order_type) params.set('order_type', filters.order_type)
  if (filters.po_ref) params.set('po_ref', filters.po_ref)
  if (filters.date_from) params.set('date_from', filters.date_from)
  if (filters.date_to) params.set('date_to', filters.date_to)
  params.set('limit', String(filters.limit ?? 50))
  params.set('offset', String(filters.offset ?? 0))
  const qs = params.toString()

  return useQuery({
    queryKey: [...UVIVA_KEY, 'submissions', filters],
    queryFn: () => jsonFetch<SubmissionListResponse>(`${BASE}/submissions?${qs}`),
    staleTime: 15_000,
  })
}

export function useSubmission(id: string | null) {
  return useQuery({
    queryKey: [...UVIVA_KEY, 'submission', id],
    queryFn: () => jsonFetch<SubmissionSummary>(`${BASE}/submissions/${id}`),
    enabled: id != null,
    staleTime: 15_000,
    // Poll while an approve/retry cycle is in flight so the drawer picks up
    // the eventual posted/post_failed outcome without the user manually
    // closing and reopening it (2026-08-03: approve/retry used to look like
    // it silently succeeded because nothing polled for the real result).
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status === 'posting' || status === 'validating' ? 5_000 : false
    },
  })
}

export function useApproveSubmission() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, note, override = false }: { id: string; note: string; override?: boolean }) =>
      jsonFetch<{ status: string; id: string }>(`${BASE}/submissions/${id}/approve`, {
        method: 'POST',
        body: { note, override },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: UVIVA_KEY })
    },
  })
}

export function useRejectSubmission() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      jsonFetch<{ status: string; id: string }>(`${BASE}/submissions/${id}/reject`, {
        method: 'POST',
        body: { note },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: UVIVA_KEY })
    },
  })
}

export function useCloseAsDuplicate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      jsonFetch<{ status: string; id: string }>(`${BASE}/submissions/${id}/close-as-duplicate`, {
        method: 'POST',
        body: { note },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: UVIVA_KEY })
    },
  })
}

export function useUpdateSubmissionNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      jsonFetch<SubmissionSummary>(`${BASE}/submissions/${id}/note`, {
        method: 'PATCH',
        body: { note },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: UVIVA_KEY })
    },
  })
}

export function useSetRoutingMode() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, routing_mode }: { id: string; routing_mode: RoutingMode | null }) =>
      jsonFetch<SubmissionSummary>(`${BASE}/submissions/${id}/routing-mode`, {
        method: 'PATCH',
        body: { routing_mode },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: UVIVA_KEY })
    },
  })
}

export function useRetrySubmission() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      jsonFetch<{ status: string; id: string }>(`${BASE}/submissions/${id}/retry`, {
        method: 'POST',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: UVIVA_KEY })
    },
  })
}

// ── Documents (BoL / Invoice / CofA) ────────────────────────────────────────────

export function useUploadSubmissionDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      submissionId,
      docType,
      file,
    }: {
      submissionId: string
      docType: DocType
      file: File
    }) => {
      const fd = new FormData()
      fd.append('doc_type', docType)
      fd.append('file', file)
      return jsonFetch<{ id: string; attached_documents: SubmissionDocument[] }>(
        `${BASE}/submissions/${submissionId}/documents`,
        { method: 'POST', rawBody: fd },
      )
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: UVIVA_KEY })
    },
  })
}

export function useDeleteSubmissionDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ submissionId, documentId }: { submissionId: string; documentId: number }) =>
      jsonFetch<{ id: string; attached_documents: SubmissionDocument[] }>(
        `${BASE}/submissions/${submissionId}/documents/${documentId}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: UVIVA_KEY })
    },
  })
}

export function useUploadLineCoaDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      submissionId,
      lineId,
      file,
    }: {
      submissionId: string
      lineId: number
      file: File
    }) => {
      const fd = new FormData()
      fd.append('file', file)
      return jsonFetch<{ line_id: number; coa_documents: SubmissionDocument[] }>(
        `${BASE}/submissions/${submissionId}/lines/${lineId}/coa`,
        { method: 'POST', rawBody: fd },
      )
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: UVIVA_KEY })
    },
  })
}

export function useDeleteLineCoaDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      submissionId,
      lineId,
      documentId,
    }: {
      submissionId: string
      lineId: number
      documentId: number
    }) =>
      jsonFetch<{ line_id: number; coa_documents: SubmissionDocument[] }>(
        `${BASE}/submissions/${submissionId}/lines/${lineId}/coa/${documentId}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: UVIVA_KEY })
    },
  })
}

// ── Ship-to contacts (Uviva Direct orders) ──────────────────────────────────────

export function useShipToContacts(enabled = true) {
  return useQuery({
    queryKey: [...UVIVA_KEY, 'ship-to-contacts'],
    queryFn: () => jsonFetch<{ contacts: ShipToContact[] }>(`${BASE}/ship-to-contacts`),
    enabled,
    staleTime: 60_000,
  })
}

export function useCreateShipToContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      name: string
      street?: string
      street2?: string
      city?: string
      zip?: string
      country_code?: string
    }) => jsonFetch<{ id: number; name: string }>(`${BASE}/ship-to-contacts`, {
      method: 'POST',
      body,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...UVIVA_KEY, 'ship-to-contacts'] })
    },
  })
}

// ── PO lookup ──────────────────────────────────────────────────────────────────

export function usePOLines(poRef: string | null) {
  return useQuery({
    queryKey: [...UVIVA_KEY, 'po-lines', poRef],
    queryFn: () => jsonFetch<POLinesResponse>(`${BASE}/orders/${encodeURIComponent(poRef ?? '')}/lines`),
    enabled: poRef != null && poRef.length > 0,
    staleTime: 60_000,
  })
}

// ── Inventory ──────────────────────────────────────────────────────────────────

export function useInventory(opts: {
  productCode?: string
  lotSerial?: string
  onlyAvailable?: boolean
} = {}) {
  const params = new URLSearchParams()
  if (opts.productCode) params.set('product_code', opts.productCode)
  if (opts.lotSerial) params.set('lot_serial', opts.lotSerial)
  if (opts.onlyAvailable) params.set('only_available', 'true')
  const qs = params.toString()

  return useQuery({
    queryKey: [...UVIVA_KEY, 'inventory', opts],
    queryFn: () => jsonFetch<InventoryResponse>(`${BASE}/inventory${qs ? `?${qs}` : ''}`),
    staleTime: 60_000,
  })
}

// ── BI Reports ─────────────────────────────────────────────────────────────────

export function useOrderHistory(dateFrom?: string, dateTo?: string) {
  const params = new URLSearchParams()
  if (dateFrom) params.set('date_from', dateFrom)
  if (dateTo) params.set('date_to', dateTo)
  const qs = params.toString()

  return useQuery({
    queryKey: [...UVIVA_KEY, 'report-order-history', dateFrom, dateTo],
    queryFn: () => jsonFetch<OrderHistoryResponse>(`${BASE}/reports/order-history${qs ? `?${qs}` : ''}`),
    staleTime: 60_000,
  })
}

export function useDeliveryPerformance(dateFrom?: string, dateTo?: string) {
  const params = new URLSearchParams()
  if (dateFrom) params.set('date_from', dateFrom)
  if (dateTo) params.set('date_to', dateTo)
  const qs = params.toString()

  return useQuery({
    queryKey: [...UVIVA_KEY, 'report-delivery-perf', dateFrom, dateTo],
    queryFn: () => jsonFetch<DeliveryPerformanceResponse>(`${BASE}/reports/delivery-performance${qs ? `?${qs}` : ''}`),
    staleTime: 60_000,
  })
}

export function useCustomerDemand(dateFrom?: string, dateTo?: string, limit?: number) {
  const params = new URLSearchParams()
  if (dateFrom) params.set('date_from', dateFrom)
  if (dateTo) params.set('date_to', dateTo)
  if (limit) params.set('limit', String(limit))
  const qs = params.toString()

  return useQuery({
    queryKey: [...UVIVA_KEY, 'report-customer-demand', dateFrom, dateTo, limit],
    queryFn: () => jsonFetch<CustomerDemandResponse>(`${BASE}/reports/customer-demand${qs ? `?${qs}` : ''}`),
    staleTime: 60_000,
  })
}

// ── Exception stats (SPI only) ─────────────────────────────────────────────────

export function useExceptionStats() {
  return useQuery({
    queryKey: [...UVIVA_KEY, 'exception-stats'],
    queryFn: () => jsonFetch<ExceptionStats>(`${BASE}/exceptions/stats`),
    staleTime: 30_000,
    refetchInterval: 60_000,
  })
}
